/**
 * A white card that fills the rest of the screen — from where it starts down to the page's bottom
 * gutter — whatever sits above it (a tab strip, a banner, an error). Its content scrolls inside it
 * (give the scrolling part `min-h-0 flex-1 overflow-auto`), so a sticky table header stays put.
 * The Rules list and the Alert List's table both use it, so the two read the same.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Never under this: a short window scrolls the page instead of squeezing the card. */
const MIN_H = 320;

/**
 * Measured in the page's own scroll space (so scrolling does not change it), again on resize and
 * whenever the page above it changes size — a banner appearing moves the card's top.
 */
export function useFillHeight() {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const main = el.closest('main');
    const fit = () => {
      const top = el.getBoundingClientRect().top + (main ? main.scrollTop : window.scrollY);
      const gutter = window.innerWidth >= 640 ? 12 : 8; // the page's p-2 / sm:p-3
      // floor and 1px spare: a fractional top must not leave the page 1px to scroll.
      const next = Math.max(MIN_H, Math.floor(window.innerHeight - top - gutter) - 1);
      setHeight((h) => (h === next ? h : next));
    };
    fit();
    window.addEventListener('resize', fit);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    const page = main?.firstElementChild;
    if (ro && page) ro.observe(page);
    if (ro && el.parentElement) ro.observe(el.parentElement);
    return () => { window.removeEventListener('resize', fit); ro?.disconnect(); };
  }, []);
  return { ref, height };
}

export default function FillHeightCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  const fill = useFillHeight();
  return (
    <div ref={fill.ref} style={fill.height ? { height: fill.height } : undefined}
      className={`flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card ${className}`}>
      {children}
    </div>
  );
}

/**
 * A list table's header inside a FillHeightCard — sticky, and opaque so rows never show through.
 * The row's tint follows one rule, the Alert List's:
 *   - the table IS the card (no search/filter strip above it inside the card) → plain white head;
 *   - a filter/toolbar strip sits above it inside the card → a muted head, so the column titles
 *     read as a separate band from the controls.
 *   <thead className={TABLE_HEAD}><tr className={tableHeadRow(hasFilterStrip)}>…
 */
export const TABLE_HEAD = 'sticky top-0 z-10 bg-card';
export const tableHeadRow = (underFilters: boolean) =>
  (underFilters ? 'border-b border-border bg-muted/40' : 'border-b border-border');
/** A table header cell — the Alert List's. */
export const TABLE_TH = 'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';
/** A clickable body row — the Alert List's hover. */
export const TABLE_ROW = 'border-b border-border transition-colors last:border-0 hover:bg-muted';
